import React, { lazy, Suspense, useEffect, useMemo } from "react";
import type { ClassKey } from "keycloakify/login";
import type { KcContext } from "./KcContext";
import { useI18n } from "./i18n";
import DefaultPage from "keycloakify/login/DefaultPage";
import Template from "./Template";
import { CountriesProvider } from "context/CountriesProvider";
import { getCountriesApi } from "../helper/mirrorsUrlHelper";
import { initializeTawkToScript } from "../helper/tawkHelper";

const UserProfileFormFields = lazy(() => import("./UserProfileFormFields"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const LoginResetPassword = lazy(() => import("./pages/LoginResetPassword"));

const doMakeUserConfirmPassword = true;

export default function KcPage(props: { kcContext: KcContext }) {
    const { kcContext } = props;

    useEffect(() => {
        const currentUrlParams = new URLSearchParams(window.location.search);
        let regUrl = currentUrlParams.get("reg_url") || localStorage.getItem("reg_url") || document.referrer;
        localStorage.setItem("reg_url", regUrl);
        kcContext.regUrl = regUrl;
    }, []);

    const { i18n } = useI18n({ kcContext });

    const countriesApiUrl = useMemo(() => {
        return getCountriesApi(
            window.location.href,
            kcContext.properties.COUNTRIES_API_URL
        );
    }, [kcContext.properties.COUNTRIES_API_URL, window.location.href]);

    useEffect(() => {
        initializeTawkToScript(
            `https://embed.tawk.to/${kcContext.properties.TAWK_TO_KEY}/default`
        );
    }, []);

    return (
        <CountriesProvider remoteUrl={countriesApiUrl}>
            <Suspense>
                {(() => {
                    switch (kcContext.pageId) {
                        case "register.ftl":
                            return (
                                <Register
                                    {...{ kcContext, i18n, classes }}
                                    Template={Template}
                                    doUseDefaultCss={true}
                                    UserProfileFormFields={UserProfileFormFields}
                                    doMakeUserConfirmPassword={doMakeUserConfirmPassword}
                                />
                            );
                        case "login.ftl":
                            return (
                                <Login
                                    {...{ kcContext, i18n, classes }}
                                    Template={Template}
                                    doUseDefaultCss={true}
                                />
                            );
                        case "login-reset-password.ftl":
                            return (
                                <LoginResetPassword
                                    {...{ kcContext, i18n, classes }}
                                    Template={Template}
                                    doUseDefaultCss={true}
                                />
                            );
                        default:
                            return (
                                <DefaultPage
                                    kcContext={kcContext}
                                    i18n={i18n}
                                    classes={classes}
                                    Template={Template}
                                    doUseDefaultCss={true}
                                    UserProfileFormFields={UserProfileFormFields}
                                    doMakeUserConfirmPassword={doMakeUserConfirmPassword}
                                />
                            );
                    }
                })()}
            </Suspense>
        </CountriesProvider>
    );
}

const classes = {} satisfies { [key in ClassKey]?: string };
